import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TrendingUp, CheckSquare, FileText, DollarSign, Sparkles, Mic, Edit } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LeadForm } from "./forms/LeadForm";
import { TaskForm } from "./forms/TaskForm";
import { InvoiceForm } from "./forms/InvoiceForm";
import { TransactionForm } from "./forms/TransactionForm";
import { AISmartEntry } from "./AISmartEntry";
import { VoiceInput } from "./VoiceInput";
import { CaptureHistorySidebar } from "./CaptureHistorySidebar";
import { useIsMobile } from "@/hooks/use-mobile";

interface QuickCaptureModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type ManualEntryType = "lead" | "task" | "invoice" | "transaction";

export const QuickCaptureModal = ({ isOpen, onClose }: QuickCaptureModalProps) => {
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState("smart");
  const [manualEntryType, setManualEntryType] = useState<ManualEntryType>("lead");
  const [voiceTranscript, setVoiceTranscript] = useState("");
  const [historyKey, setHistoryKey] = useState(0);

  const handleVoiceTranscript = (text: string) => {
    setVoiceTranscript(text);
    setActiveTab("smart");
  };

  const handleSuccess = () => {
    setHistoryKey(prev => prev + 1);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={`${isMobile ? 'w-full h-full max-w-none max-h-none rounded-none' : 'sm:max-w-[900px] max-h-[90vh]'} p-0`}>
        <div className={`${isMobile ? 'flex flex-col h-full' : 'grid grid-cols-1 md:grid-cols-[1fr,300px] gap-0 max-h-[90vh]'}`}>
          {/* Main Form Area */}
          <div className={`${isMobile ? 'flex-1 overflow-y-auto' : ''} p-4 sm:p-6`}>
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-primary">Quick Capture</DialogTitle>
            </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3 h-auto">
            <TabsTrigger value="smart" className="flex flex-col items-center gap-1 py-2">
              <Sparkles className="h-4 w-4" />
              <span className="text-xs">Smart</span>
            </TabsTrigger>
            <TabsTrigger value="voice" className="flex flex-col items-center gap-1 py-2">
              <Mic className="h-4 w-4" />
              <span className="text-xs">Voice</span>
            </TabsTrigger>
            <TabsTrigger value="manual" className="flex flex-col items-center gap-1 py-2">
              <Edit className="h-4 w-4" />
              <span className="text-xs">Manual</span>
            </TabsTrigger>
          </TabsList>

            <div className="mt-4">
              <TabsContent value="smart">
                <AISmartEntry onSuccess={handleSuccess} initialText={voiceTranscript} />
              </TabsContent>

              <TabsContent value="voice">
                <VoiceInput onTranscript={handleVoiceTranscript} />
              </TabsContent>

              <TabsContent value="manual" className="space-y-4">
                <div className="grid grid-cols-4 gap-2">
                  <Button
                    variant={manualEntryType === "lead" ? "default" : "outline"}
                    onClick={() => setManualEntryType("lead")}
                    className="flex flex-col items-center gap-1 h-auto py-3"
                  >
                    <TrendingUp className="h-5 w-5" />
                    <span className="text-xs">Lead</span>
                  </Button>
                  <Button
                    variant={manualEntryType === "task" ? "default" : "outline"}
                    onClick={() => setManualEntryType("task")}
                    className="flex flex-col items-center gap-1 h-auto py-3"
                  >
                    <CheckSquare className="h-5 w-5" />
                    <span className="text-xs">Task</span>
                  </Button>
                  <Button
                    variant={manualEntryType === "invoice" ? "default" : "outline"}
                    onClick={() => setManualEntryType("invoice")}
                    className="flex flex-col items-center gap-1 h-auto py-3"
                  >
                    <FileText className="h-5 w-5" />
                    <span className="text-xs">Invoice</span>
                  </Button>
                  <Button
                    variant={manualEntryType === "transaction" ? "default" : "outline"}
                    onClick={() => setManualEntryType("transaction")}
                    className="flex flex-col items-center gap-1 h-auto py-3"
                  >
                    <DollarSign className="h-5 w-5" />
                    <span className="text-xs">Money</span>
                  </Button>
                </div>

                <div className="mt-4">
                  {manualEntryType === "lead" && <LeadForm onSuccess={handleSuccess} />}
                  {manualEntryType === "task" && <TaskForm onSuccess={handleSuccess} />}
                  {manualEntryType === "invoice" && <InvoiceForm onSuccess={handleSuccess} />}
                  {manualEntryType === "transaction" && <TransactionForm onSuccess={handleSuccess} />}
                </div>
              </TabsContent>
            </div>
          </Tabs>
          </div>

          {/* History Sidebar - Hidden on mobile */}
          {!isMobile && (
            <div className="border-l bg-muted/20 overflow-y-auto">
              <CaptureHistorySidebar key={historyKey} onUpdate={() => setHistoryKey(prev => prev + 1)} />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
